import { ActorRefFrom } from 'xstate';
import { useSelector } from '@xstate/react';
import {
    HStack, IconButton, Text, VStack,
} from '@chakra-ui/react';
import { FaIcon } from '@acp/core-ui';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import React, { ComponentProps } from 'react';
import { documentUploadMachine } from './machine';

function DocumentUploadItem({
    actorRef,
    ...props
}: {
    actorRef: ActorRefFrom<typeof documentUploadMachine>;
} & ComponentProps<typeof HStack>) {
    const isUploading = useSelector(actorRef, (state) => state.matches('uploading'));
    const isQueued = useSelector(actorRef, (state) => state.matches('queued'));
    const isFailed = useSelector(actorRef, (state) => state.matches('failed'));
    const isSuccess = useSelector(actorRef, (state) => state.matches('success'));
    const uploadPercentage = useSelector(actorRef, (state) => state.context.uploadPercentage);
    const fileName = useSelector(actorRef, (state) => state.context.fileName);
    const error = useSelector(actorRef, (state) => state.context.error);

    return (
        <HStack
            {...props}
        >
            <VStack flexGrow={1} alignItems="start" gap={0} overflow="hidden">
                <Text textOverflow="ellipsis" whiteSpace="nowrap" overflow="hidden" fontWeight="medium" width="100%">
                    {fileName}
                </Text>
                {
                    isQueued && (
                        <Text color="muted.500" fontSize="sm">
                            Queued
                        </Text>
                    )
                }
                {
                    isUploading && uploadPercentage < 100 && (
                        <Text color="info.500" fontSize="sm">
                            Uploading... (
                            {uploadPercentage}
                            %)
                        </Text>
                    )
                }
                {
                    isUploading && uploadPercentage >= 100 && (
                        <Text color="info.500" fontSize="sm">
                            Processing...
                        </Text>
                    )
                }
                {
                    isFailed && (
                        <Text color="danger.500" fontSize="sm">
                            Failed (
                            {error}
                            )
                        </Text>
                    )
                }
                {
                    isSuccess && (
                        <Text color="success.500" fontSize="sm">
                            Uploaded (100%)
                        </Text>
                    )
                }
            </VStack>
            <IconButton
                aria-label="Cancel"
                variant="ghost"
                size="sm"
                colorScheme="muted"
                icon={<FaIcon icon={faTimes} />}
                onClick={() => actorRef.send({ type: 'CANCEL' })}
            />
        </HStack>
    );
}

const DocumentUploadItemMemo = React.memo(
    DocumentUploadItem,
    (prevProps, nextProps) => prevProps.actorRef.id === nextProps.actorRef.id
);

export { DocumentUploadItemMemo as DocumentUploadItem };
